import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import backIcon from "../assets/ecommerce/atras.png";
import cartIcon from "../assets/ecommerce/carrito.webp";
import { useCart } from "../context/CartContext";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://localhost:89").replace(
  /\/$/,
  ""
);

const formatCurrency = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const buildApiUrl = (path) => `${API_BASE_URL}${path}`;

const normalizeFlakeCode = (value) => String(value || "").trim().toUpperCase();

const EcommerceFlakes = () => {
  const { addItem, openCart, shippingConfig } = useCart();
  const [searchParams] = useSearchParams();
  const [flakes, setFlakes] = useState([]);
  const [selectedFlake, setSelectedFlake] = useState(null);
  const [loadingFlakes, setLoadingFlakes] = useState(true);
  const [flakesError, setFlakesError] = useState("");
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState("");
  const [pricingLoading, setPricingLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("flakes");
  const [flakeImageSrc, setFlakeImageSrc] = useState(null);
  const requestedFlakeCode = useMemo(
    () => normalizeFlakeCode(searchParams.get("code")),
    [searchParams]
  );

  useEffect(() => {
    let active = true;
    setLoadingFlakes(true);

    fetch(buildApiUrl("/api/ecommerce/flakes/products"))
      .then(async (response) => {
        const contentType = response.headers.get("content-type") || "";
        const text = await response.text();
        if (!response.ok || !contentType.includes("application/json")) {
          throw new Error("No se pudieron cargar los flakes.");
        }
        try {
          return JSON.parse(text);
        } catch (error) {
          throw new Error("No se pudieron cargar los flakes.");
        }
      })
      .then((data) => {
        if (!active) return;
        setFlakes(data);
        setSelectedFlake(data[0] || null);
      })
      .catch((error) => {
        if (!active) return;
        setFlakesError(error.message);
      })
      .finally(() => {
        if (!active) return;
        setLoadingFlakes(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (loadingFlakes || !flakes.length || !requestedFlakeCode) return;

    const matchedFlake = flakes.find(
      (flake) => normalizeFlakeCode(flake.code) === requestedFlakeCode
    );

    if (!matchedFlake) return;

    setSelectedFlake((current) => (current?.id === matchedFlake.id ? current : matchedFlake));
    setActiveTab("summary");
  }, [flakes, loadingFlakes, requestedFlakeCode]);

  useEffect(() => {
    if (loadingFlakes || !selectedFlake) {
      setFlakeImageSrc(null);
      return;
    }

    const src = selectedFlake?.image_url;
    if (!src) {
      setFlakeImageSrc("/ecommerce/Flakes.png");
      return;
    }

    let active = true;
    setFlakeImageSrc(null);

    const img = new Image();
    img.onload = () => {
      if (!active) return;
      setFlakeImageSrc(src);
    };
    img.onerror = () => {
      if (!active) return;
      setFlakeImageSrc("/ecommerce/Flakes.png");
    };
    img.src = src;

    return () => {
      active = false;
    };
  }, [loadingFlakes, selectedFlake]);

  useEffect(() => {
    setPricing(null);
    setPricingError("");

    if (!selectedFlake) {
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      setPricingLoading(true);

      fetch(buildApiUrl("/api/ecommerce/flakes/pricing"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          flake_id: selectedFlake.id,
        }),
        signal: controller.signal,
      })
        .then(async (response) => {
          const contentType = response.headers.get("content-type") || "";
          const text = await response.text();
          if (!response.ok || !contentType.includes("application/json")) {
            throw new Error("No se pudo calcular el precio.");
          }
          try {
            return JSON.parse(text);
          } catch (error) {
            throw new Error("No se pudo calcular el precio.");
          }
        })
        .then((data) => {
          setPricing(data);
        })
        .catch((error) => {
          if (error.name === "AbortError") return;
          setPricingError(error.message);
        })
        .finally(() => {
          setPricingLoading(false);
        });
    }, 200);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [selectedFlake]);

  const summary = useMemo(() => {
    if (!pricing) return "--";
    return formatCurrency.format(pricing.final_cost || 0);
  }, [pricing]);

  const shippingLabel = useMemo(() => {
    if (!pricing) return "--";
    const finalCost = Number(pricing.final_cost);
    if (!Number.isFinite(finalCost)) return "--";
    const threshold = shippingConfig?.threshold ?? 499;
    const cost = shippingConfig?.cost ?? 199;
    return finalCost >= threshold ? "Gratis" : formatCurrency.format(cost);
  }, [pricing, shippingConfig]);

  const handleAddToCart = () => {
    if (!selectedFlake || !pricing) return;
    addItem({
      type: "flakes",
      name: selectedFlake.type,
      unitPrice: pricing.final_cost || 0,
      payload: {
        flake_id: selectedFlake.id,
      },
    });
    openCart();
  };

  const goPrevTab = () => {
    if (activeTab === "summary") {
      setActiveTab("flakes");
    }
  };

  const goNextTab = () => {
    if (activeTab === "flakes") {
      setActiveTab("summary");
    }
  };

  return (
    <div className="relative px-8 md:px-14 py-6 md:py-8 overflow-hidden h-auto md:h-full flex flex-col bg-white">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col flex-1">
        <div className="grid grid-cols-2 grid-rows-[auto,auto] items-center gap-3 md:gap-4 mb-5 md:mb-6 md:grid-cols-[160px,1fr,160px] md:grid-rows-1">
          <Link
            to="/ecommerce"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 justify-self-start"
          >
            <img src={backIcon} alt="Atrás" className="w-8 h-8 md:w-10 md:h-10" />
            <span className="text-sm md:text-base font-semibold">Atrás</span>
          </Link>
          <button
            type="button"
            onClick={openCart}
            className="justify-self-end cursor-pointer md:col-start-3"
          >
            <img src={cartIcon} alt="Carrito" className="w-14 h-14 md:w-16 md:h-16" />
          </button>
          <h1 className="col-span-2 row-start-2 text-xl md:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-gray-900 text-center max-w-[420px] md:max-w-[1000px] w-full justify-self-center leading-tight md:col-span-1 md:col-start-2 md:row-start-1">
            Compra flakes para personalizar tu proyecto
          </h1>
        </div>

        <div className="flex flex-col md:flex-row items-start gap-2 md:gap-3 flex-1 min-h-0">
          <div className="md:w-[50%] w-full h-[38vh] md:h-full shrink-0">
            <div className="relative h-full flex items-start justify-end md:pt-2">
              <div className="relative inline-flex items-start justify-start w-[520px] h-[320px] md:w-[620px] md:h-[500px] overflow-visible">
                {flakeImageSrc && (
                  <img
                    src={flakeImageSrc}
                    alt={selectedFlake?.type || "Flakes"}
                    className="block w-full h-full object-contain"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="md:w-[50%] w-full md:flex-1 min-h-0 md:h-full">
            <div className="h-full min-h-0 flex flex-col">
              <div className="flex items-center justify-between bg-black text-white px-4 py-2 rounded-lg">
                <button
                  type="button"
                  className="text-white/70"
                  aria-label="Paso anterior"
                  onClick={goPrevTab}
                >
                  <img src={backIcon} alt="Atrás" className="w-5 h-5 md:w-6 md:h-6" />
                </button>
                <div className="flex items-center justify-evenly flex-1 mx-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab("flakes")}
                    className={`px-4 py-1 text-xs font-bold rounded-sm shadow-sm ${
                      activeTab === "flakes" ? "bg-white text-black" : "text-white/50"
                    }`}
                  >
                    FLAKES
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("summary")}
                    className={`px-4 py-1 text-xs font-bold rounded-sm shadow-sm ${
                      activeTab === "summary" ? "bg-white text-black" : "text-white/50"
                    }`}
                  >
                    RESUMEN
                  </button>
                </div>
                <button
                  type="button"
                  className="text-white/70"
                  aria-label="Paso siguiente"
                  onClick={goNextTab}
                >
                  <img
                    src={backIcon}
                    alt="Siguiente"
                    className="w-5 h-5 md:w-6 md:h-6 rotate-180"
                  />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-5 py-4 pb-10 md:pb-12 space-y-4">
                {activeTab === "flakes" ? (
                  <>
                    <div className="rounded-xl border border-black/30 bg-white/80 px-3 py-3 space-y-3">
                      <h2 className="text-sm md:text-base font-semibold text-gray-900 text-left">
                        Selecciona el color de tus flakes:
                      </h2>
                      {loadingFlakes ? (
                        <div className="text-sm text-gray-500">Cargando flakes...</div>
                      ) : flakesError ? (
                        <div className="text-xs text-red-600 font-semibold">{flakesError}</div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {flakes.map((flake) => {
                            const isSelected = selectedFlake?.id === flake.id;
                            return (
                              <button
                                key={flake.id}
                                type="button"
                                onClick={() => setSelectedFlake(flake)}
                                className={`px-3 py-1.5 text-[11px] md:text-xs font-bold uppercase tracking-wide rounded-full border transition ${
                                  isSelected
                                    ? "bg-white border-black text-primary-green shadow"
                                    : "bg-black border-black text-white hover:bg-gray-900"
                                }`}
                              >
                                {flake.type}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {pricingError && (
                        <p className="text-xs text-red-600 font-semibold">{pricingError}</p>
                      )}
                    </div>

                    <div className="rounded-xl bg-dark-gray text-white px-4 py-3 space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>Subtotal:</span>
                        <span className="font-bold">
                          {pricingLoading ? "Calculando..." : summary}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-white/70">
                        <span>Envío:</span>
                        <span>{pricingLoading ? "Calculando..." : shippingLabel}</span>
                      </div>
                    </div>

                    <div className="flex justify-end pb-2">
                      <button
                        type="button"
                        disabled={!selectedFlake}
                        onClick={() => setActiveTab("summary")}
                        className="px-6 py-2 rounded-full bg-primary-green text-white font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-6 text-left">
                    <div className="space-y-2 w-full">
                      <div className="text-2xl md:text-3xl font-extrabold uppercase text-gray-900">
                        {selectedFlake?.type || "FLAKES"}
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                      <div className="space-y-2">
                        <div className="text-base md:text-lg font-semibold text-gray-700">
                          Envío:
                        </div>
                        <div
                          className={`text-2xl md:text-3xl font-extrabold ${
                            shippingLabel === "Gratis" ? "text-primary-green" : "text-gray-900"
                          }`}
                        >
                          {pricingLoading ? "Calculando..." : shippingLabel}
                        </div>

                        <div className="pt-3 space-y-1">
                          <div className="text-base md:text-lg font-semibold text-gray-700">
                            Subtotal:
                          </div>
                          <div className="text-3xl md:text-4xl font-extrabold text-gray-900">
                            {pricingLoading ? "Calculando..." : summary}
                          </div>
                        </div>
                      </div>

                    <div className="flex flex-col gap-3 md:items-end">
                      <button
                        type="button"
                        onClick={handleAddToCart}
                        className="px-8 py-2.5 rounded-full bg-pink-500 text-white font-bold shadow-md w-fit text-base md:text-lg"
                      >
                        Añadir al carrito
                      </button>
                    </div>
                    </div>

                    <div className="text-xl md:text-2xl font-extrabold text-gray-900">RESUMEN</div>
                    <div className="rounded-xl bg-dark-gray text-white px-5 py-4 text-base">
                      • {selectedFlake?.type || ""}.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EcommerceFlakes;
